import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { api, fmtEUR } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { ChevronLeft, ChevronRight, Printer, FolderPlus, TrendingUp, TrendingDown } from 'lucide-react';

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export default function Reporting() {
  const today = new Date();
  const [annee, setAnnee] = useState(today.getFullYear());
  const [mois, setMois] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);

  const load = async () => {
    const { data: d } = await api.get(`/dashboard/reporting-mensuel?annee=${annee}&mois=${mois}`);
    setData(d);
  };
  useEffect(() => { load(); }, [annee, mois]);

  const years = Array.from({ length: 6 }, (_, i) => today.getFullYear() - 2 + i);
  const prev = () => { if (mois === 1) { setMois(12); setAnnee(a => a - 1); } else setMois(m => m - 1); };
  const next = () => { if (mois === 12) { setMois(1); setAnnee(a => a + 1); } else setMois(m => m + 1); };
  const periodeLabel = `${MONTHS[mois - 1]} ${annee}`;

  // Projection EOY rémunération dirigeants
  const projection = useMemo(() => {
    if (!data || data.remu_dirig_annee === 0) return null;
    return data.remu_dirig_annee * 12 / Math.max(mois, 1); // extrapolation simple
  }, [data, mois]);

  const handlePrint = () => window.print();

  return (
    <Layout
      title="Reporting mensuel"
      subtitle={`Synthèse agence — ${periodeLabel}`}
      actions={
        <div className="flex items-center gap-2 print:hidden">
          <button onClick={prev} className="p-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white" data-testid="reporting-prev"><ChevronLeft size={16} /></button>
          <Select value={String(mois)} onValueChange={(v) => setMois(Number(v))}>
            <SelectTrigger className="w-32 rounded-none border-zinc-900" data-testid="reporting-mois"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(annee)} onValueChange={(v) => setAnnee(Number(v))}>
            <SelectTrigger className="w-24 rounded-none border-zinc-900" data-testid="reporting-annee"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={next} className="p-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white" data-testid="reporting-next"><ChevronRight size={16} /></button>
          <Button onClick={handlePrint} className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="reporting-print">
            <Printer size={14} className="mr-1.5" /> Imprimer / PDF
          </Button>
        </div>
      }
    >
      {!data ? (
        <div className="text-center text-zinc-500 py-12">Chargement…</div>
      ) : (
        <div className="space-y-6">
          {/* Bandeau résumé */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KpiCard label="CA encaissé mois" value={data.ca_mois} ytd={data.ca_cumul} ytdLabel="YTD" testid="kpi-ca-mois" color="emerald" />
            <KpiCard label="CA mérité mois" value={data.merite_mois} ytd={data.merite_cumul} ytdLabel="YTD" testid="kpi-merite-mois" color="blue" />
            <KpiCard label="Dépenses mois" value={data.depenses_mois + data.cout_pointages_mois} ytd={data.depenses_cumul + data.cout_pointages_cumul} ytdLabel="YTD" testid="kpi-depenses-mois" color="amber" />
            <KpiCard label="Marge mois" value={data.marge_mois} ytd={data.marge_cumul} ytdLabel="YTD" testid="kpi-marge-mois" color={data.marge_mois >= 0 ? 'emerald' : 'rose'} />
          </div>

          {/* Nouveaux projets */}
          <Section title={`Nouveaux projets — ${data.nb_nouveaux_projets} créés ce mois`} icon={FolderPlus}>
            {data.nouveaux_projets.length === 0 ? (
              <div className="px-4 py-6 text-center text-zinc-500 text-xs italic">Aucun nouveau projet ce mois.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-zinc-200">
                  <tr className="text-left">
                    <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Code</th>
                    <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Promo</th>
                    <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Nom</th>
                    <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Statut</th>
                    <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Travaux</th>
                    <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Honoraires</th>
                    <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.nouveaux_projets.map((p) => (
                    <tr key={p.code} className="border-b border-zinc-100">
                      <td className="px-3 py-2 font-mono text-xs font-medium">{p.code}</td>
                      <td className="px-3 py-2 font-mono text-xs text-zinc-500">{p.promoteur_code || '—'}</td>
                      <td className="px-3 py-2 text-xs">{p.nom}</td>
                      <td className="px-3 py-2 text-[10px] text-zinc-600">{p.statut}</td>
                      <td className="px-3 py-2 font-mono text-right text-xs">{fmtEUR(p.montant_travaux)}</td>
                      <td className="px-3 py-2 font-mono text-right text-[#002FA7] text-xs">{fmtEUR(p.honoraires)}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{p.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Détail dépenses */}
          <Section title="Détail dépenses agence" icon={TrendingDown}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
              <DetailLine label="Coût heures pointées (mois)" value={data.cout_pointages_mois} sub={`YTD : ${fmtEUR(data.cout_pointages_cumul)}`} />
              <DetailLine label="Charges saisies (mois)" value={data.depenses_mois} sub={`YTD : ${fmtEUR(data.depenses_cumul)}`} />
              <DetailLine label="Salaires fixes mensuels (info)" value={data.salaires_mensuel} sub={`Annuel : ${fmtEUR(data.salaires_mensuel * 12)}`} />
              <DetailLine label="Assurance — provision mensuelle" value={data.assurance_mois} sub={`Total estimée portefeuille : ${fmtEUR(data.assurance_totale_estimee)}`} highlight="amber" />
              <DetailLine label="Rémunération dirigeants — année" value={data.remu_dirig_annee} sub={projection ? `Projection EOY : ${fmtEUR(projection)}` : 'Pas de projection (aucun mois actif)'} highlight="violet" />
              <DetailLine label="Rémunération dirigeants — portefeuille total" value={data.remu_dirig_total_portefeuille} sub="Tous projets confondus" />
            </div>
          </Section>

          {/* Synthèse marge */}
          <Section title="Synthèse marge agence" icon={TrendingUp}>
            <div className="p-6 space-y-3">
              <SyntheseRow label="CA encaissé" value={data.ca_mois} positive />
              <SyntheseRow label="– Charges saisies" value={data.depenses_mois} />
              <SyntheseRow label="– Coût pointages" value={data.cout_pointages_mois} />
              <SyntheseRow label="– Provision assurance (1/12)" value={data.assurance_mois} />
              <div className="border-t-2 border-zinc-900 pt-3 mt-2">
                <div className={`flex justify-between font-mono font-semibold text-lg ${data.marge_mois >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                  <span>Marge agence du mois</span>
                  <span data-testid="reporting-marge-mois">{fmtEUR(data.marge_mois)}</span>
                </div>
                <div className={`flex justify-between font-mono text-sm mt-2 ${data.marge_cumul >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                  <span>Marge cumulée YTD</span>
                  <span data-testid="reporting-marge-cumul">{fmtEUR(data.marge_cumul)}</span>
                </div>
              </div>
            </div>
          </Section>

          <div className="text-[10px] text-zinc-400 italic text-center pt-4">
            Reporting généré automatiquement le {new Date().toLocaleDateString('fr-FR')} — ATELIER MN ARCHITECTURE
          </div>
        </div>
      )}
    </Layout>
  );
}

function KpiCard({ label, value, ytd, ytdLabel, testid, color = 'blue' }) {
  const colorMap = {
    emerald: 'border-l-emerald-500 text-emerald-700',
    blue: 'border-l-[#002FA7] text-[#002FA7]',
    amber: 'border-l-amber-400 text-amber-700',
    rose: 'border-l-rose-500 text-rose-700',
  };
  return (
    <div className={`bg-white border border-zinc-200 border-l-4 ${colorMap[color]} p-4`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className="font-mono font-medium text-2xl">{fmtEUR(value)}</div>
      <div className="text-[10px] text-zinc-500 mt-1">{ytdLabel} : {fmtEUR(ytd)}</div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-zinc-200">
      <div className="border-b border-zinc-900 px-4 py-3 flex items-center gap-2">
        {Icon && <Icon size={14} className="text-zinc-700" />}
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-700 font-medium">{title}</div>
      </div>
      {children}
    </div>
  );
}

function DetailLine({ label, value, sub, highlight }) {
  const hl = highlight === 'amber' ? 'bg-amber-50/40' : highlight === 'violet' ? 'bg-violet-50/40' : '';
  return (
    <div className={`p-4 border-r border-b border-zinc-100 ${hl}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-1">{label}</div>
      <div className="font-mono font-medium text-lg">{fmtEUR(value)}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function SyntheseRow({ label, value, positive }) {
  return (
    <div className="flex justify-between font-mono text-sm">
      <span className="text-zinc-700">{label}</span>
      <span className={positive ? 'text-emerald-700 font-semibold' : ''}>{fmtEUR(value)}</span>
    </div>
  );
}
